import React, { useState, useRef, useEffect } from 'react';
import { PdfLoader, PdfHighlighter, Popup, AreaHighlight } from 'react-pdf-highlighter';
import { LinearProgress, Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../../UI/AlertDialog';
import PropTypes from 'prop-types';
import GraphqlApi from '../../../api-graphql';
import { useTour } from '@reactour/tour';

function PdfComponent (props) {
  const [pdfError, setPdfError] = useState(null);
  const { t } = useTranslation();
  const { url, item, index, type, customFlow } = props;
  const pdfRef = useRef();
  const [numPages, setNumPages] = useState(1);
  const [disableAccept, setDisableAccept] = useState(false);
  const { setCurrentStep } = useTour();
  let iniSignature;
  if (!customFlow) {
    iniSignature = (index === 0 && item.principal && item.principal.signaturesPositions)
      ? item.principal.signaturesPositions.map((elem, position) => ({ id: position, position: elem }))
      : (index !== 0 && item.annexes && item.annexes.length >= index && item.annexes[index - 1].signaturesPositions)
        ? item.annexes[index - 1].signaturesPositions.map((elem, position) => ({ id: position, position: elem }))
        : type === 'template' && item.signaturesPositions && item.signaturesPositions
          ? item.signaturesPositions.map((elem, position) => ({ id: position, position: elem }))
          : [];
  } else {
    iniSignature = [];
  }

  const [highlight, setHighlight] = useState(iniSignature);

  useEffect(() => {
    setCurrentStep(3);
  }, []);

  const saveDatabase = async (item, highlight) => {
    highlight.forEach((elem) => {
      if (elem && elem.position && elem.position.boundingRect && elem.position.boundingRect.pageNumber) { delete elem.position.boundingRect.pageNumber; }
    });
    setHighlight(JSON.parse(JSON.stringify(highlight)));

    if (item.status === 'INIT') {
      if (index === 0 && item.principal) {
        item.principal.signaturesPositions = highlight.map((elem) => elem.position);
      } else if (index !== 0 && item.annexes && item.annexes.length >= index) {
        item.annexes[index - 1].signaturesPositions = highlight.map((elem) => elem.position);
      }

      item.updatedAt = new Date().toISOString();

      if (type === 'template') {
        item.signaturesPositions = highlight.map((elem) => elem.position);
        await GraphqlApi.updateTemplate(item, item.subgroups[0]).catch(console.error);
      } else {
        await GraphqlApi.updateDocument(item).catch(console.error);
      }
      setHighlight(highlight);
    }
  };

  const resetHash = () => {
    document.location.hash = '';
  };

  const addHighlight = async (newHighlight) => {
    newHighlight.id = highlight.length;
    highlight.push(newHighlight);
    saveDatabase(item, highlight);
  };

  function handleSelectionFinished () {
    const [position, content, hideTipAndSelection] = arguments;

    if (content.text) return;

    return (
      <button
        className='MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedPrimary'
        onClick={() => {
          addHighlight({ position });
          hideTipAndSelection();
        }}
      >
        {t('viewer:add')}
      </button>
    );
  }

  const updateHighlight = async (highlightId, position) => {
    const indexNew = highlight.findIndex((elem) => elem.id === highlightId);
    highlight[indexNew].position = { ...highlight[indexNew].position, ...position };

    saveDatabase(item, highlight);
  };

  function deleteHighlight () {
    while (highlight.length > 0) {
      highlight.pop();
    }
    setHighlight(highlight);
    saveDatabase(item, []);
  }

  const HighlightPopup = () => (
    <button
      className='MuiButtonBase-root MuiButton-root MuiButton-contained MuiButton-containedPrimary'
      onClick={deleteHighlight}
    >
      {t('viewer:delete')}
    </button>
  );

  function handleHighlightTransform () {
    const [highlight, index, setTip, hideTip, viewportToScaled] = arguments;

    const component = (
      <AreaHighlight
        highlight={highlight}
        style={{
          alignContent: 'center'
        }}
        onChange={(boundingRect) => {
          let pageNumber = highlight.position.pageNumber;

          const height = viewportToScaled(boundingRect).height;
          const width = viewportToScaled(boundingRect).width;

          if (boundingRect.left < 0) {
            boundingRect.left = 0;
          } else
          if (boundingRect.left + boundingRect.width > width) {
            boundingRect.left = width - boundingRect.width;
          }

          if (boundingRect.top + boundingRect.height > height) {
            if (pageNumber < numPages) {
              pageNumber++;
              boundingRect.top = 0;
            } else {
              boundingRect.top = height - boundingRect.height;
            }
          } else if (boundingRect.top < 0) {
            if (pageNumber > 1) {
              pageNumber--;
              boundingRect.top = height - boundingRect.height;
            } else {
              boundingRect.top = 0;
            }
          }
          updateHighlight(
            highlight.id,
            { boundingRect: viewportToScaled(boundingRect), pageNumber }
          );
        }}
      ><div>{highlight.id + 1}</div></AreaHighlight>
    );

    return (
      <Popup
        popupContent={<HighlightPopup {...highlight} />}
        onMouseOver={(popupContent) =>
          setTip(highlight, () => popupContent)}
        onMouseOut={hideTip}
        key={index}
      >
        {component}
      </Popup>
    );
  }

  const handlePdfError = (e) => {
    if (e.name === 'MissingPDFException') {
      setDisableAccept(true);
      setPdfError(t('pdfComponent:pdfError'));
      setTimeout(() => {
        setDisableAccept(false);
      }, 10000);
    }
  };

  const handleSaveForm = async () => {
    // check if the form has been modified
    const modified = pdfRef.current.annotationStorage._modified;

    if (modified) {
      var pages = pdfRef.current.numPages;
      var currentPageForm = {};
      for (var i = 1; i <= pages; i++) {
        var page = await pdfRef.current.getPage(i);
        var annotations = await page.getAnnotations();
        for (var j = 0; j < annotations.length; j++) {
          var element = annotations[j];
          // Field Name
          if (currentPageForm[element.fieldName] == null) {
            currentPageForm[element.fieldName] = null;
          }

          const val = pdfRef.current.annotationStorage._storage.get(element.id);
          if (element.radioButton) {
            if (val != null && val.value === true) {
              currentPageForm[element.fieldName] = element.buttonValue;
            }
          } else if (element.checkBox) {
            currentPageForm[element.fieldName] = (val != null && val.value === true) ? element.exportValue : undefined;
          } else {
            currentPageForm[element.fieldName] = (val != null && val.value !== '') ? val : undefined;
          }
        }
      }
      if (Object.keys(currentPageForm).length > 0) {
        item.defaultValues = JSON.stringify(Object.fromEntries(pdfRef.current.annotationStorage._storage));
        item.defaultValuesForm = JSON.stringify(currentPageForm);
        await GraphqlApi.updateTemplate(item, item.subgroups[0]).catch(console.error);
      }
    }
  };

  const handleLoadDocuments = (pdfDocument) => {
    pdfRef.current = pdfDocument;
    setNumPages(pdfRef.current._pdfInfo.numPages);

    if (item.defaultValues) { pdfRef.current.annotationStorage._storage = new Map(Object.entries(JSON.parse(item.defaultValues))); }
    return (
      <PdfHighlighter
        pdfDocument={pdfDocument}
        enableAreaSelection={(event) => event.ctrlKey && item.status === 'INIT'}
        scrollRef={() => {
          // setScrollViewerTo(scrollTo);
          // scrollToHighlightFromHash();
        }}
        onScrollChange={resetHash}
        onSelectionFinished={handleSelectionFinished}
        highlightTransform={handleHighlightTransform}
        highlights={highlight}
      />);
  };

  const disableAcceptButtonTimeout = () => {
    setPdfError(null);
  };

  console.log('pdfError', pdfError);
  return (
      <>
        <AlertDialog
          open={!!pdfError}
          title='Message'
          description={pdfError}
          customAccept={t('alertDialog:reload')}
          setOpen={() => disableAcceptButtonTimeout()}
          disableAccept={disableAccept}
          disableCancel />
        {!pdfError &&
          <PdfLoader url={url} beforeLoad={<LinearProgress />} onError={handlePdfError}>
            {handleLoadDocuments}
          </PdfLoader>
        }
        {type === 'template' && /[.]\w{3,4}$/.exec(item.fileUrl)[0] === '.pdf' &&
        <Button
          variant='contained'
          color='primary'
          onClick={() => handleSaveForm()}
          style={{ zIndex: 9999 }}>
          {t('templateEdit:save')}
        </Button>}
      </>
  );
}

PdfComponent.propTypes = {
  url: PropTypes.string,
  item: PropTypes.object,
  index: PropTypes.number,
  type: PropTypes.string
};

export default PdfComponent;
